import React, { useState, useRef } from "react";
import {
  Popper,
  Paper,
  Typography,
  Box,
  IconButton,
  ClickAwayListener,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CopyableText from "./CopiedText";
import { COLORS } from "../assets/theme";

interface CopyableTextWithDialogProps {
  text?: string;
  header: string;
  concatenatedFullText?: string;
  isConcatenated?: boolean;
}

const CopyableTextWithDialog: React.FC<CopyableTextWithDialogProps> = ({
  text,
  header,
  concatenatedFullText,
  isConcatenated = false,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showTimeout, setShowTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }

    if (text && text.trim() && text.length > 10) {
      const timeout = setTimeout(() => {
        setOpen(true);
        setShowTimeout(null);
      }, 200);
      setShowTimeout(timeout);
    }
  };

  const handleMouseLeave = () => {
    // clear pending show timeout
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }

    // close timeout
    const timeout = setTimeout(() => {
      setOpen(false);
    }, 300);
    setHoverTimeout(timeout);
  };

  const handlePopperMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
  };

  const handlePopperMouseLeave = () => {
    setOpen(false);
  };

  const handleClose = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setOpen(false);
  };

  const handleClickAway = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      setShowTimeout(null);
    }
    setOpen(false);
  };

  const safeText = text != null ? String(text) : "";

  // Function to render text with highlighting
  const renderTextContent = () => {
    if (isConcatenated && concatenatedFullText) {
      const fullText = String(concatenatedFullText);
      
      // If no text to highlight, just return the full text
      if (!safeText) {
        return fullText || "No content available";
      }

      // Find the index of the original text in the concatenated text
      const index = fullText.indexOf(safeText);
      
      if (index === -1) {
        // If text is not found, just return the full text
        return fullText;
      }

      // Split the text into parts: before, highlighted, and after
      const beforeText = fullText.substring(0, index);
      const highlightedText = fullText.substring(index, index + safeText.length);
      const afterText = fullText.substring(index + safeText.length);

      return (
        <>
          <span style={{ color: COLORS.placeHolderColor }}>
            {beforeText}
          </span>
          <span style={{ color: COLORS.typographyColor, fontWeight: "bold" }}>
            {highlightedText}
          </span>
          <span style={{ color: COLORS.placeHolderColor }}>
            {afterText}
          </span>
        </>
      );
    } else {
      // If not concatenated, just display the original text
      return safeText || "No content available";
    }
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "relative",
          display: "inline-block",
          cursor: safeText.length > 10 ? "pointer" : "default",
        }}
      >
        <CopyableText text={text} />
      </Box>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-start"
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            onMouseEnter={handlePopperMouseEnter}
            onMouseLeave={handlePopperMouseLeave}
            sx={{
              p: 2,
              maxWidth: 500,
              maxHeight: 300,
              boxShadow: 3,
              border: "1px solid #ccc",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {header}
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  p: 0.5,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              component="div"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 200,
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                backgroundColor: "#f8f9fa",
                padding: 1.5,
                borderRadius: 1,
                border: "1px solid #e9ecef",
              }}
            >
              {renderTextContent()}
            </Typography>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default CopyableTextWithDialog;
